import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AcceptCircleInviteRequest,
  AcceptCircleInviteResponse,
  CircleDuration,
  CircleInvite as BaseCircleInvite,
  CircleInvitePreviewResponse,
} from '@dreamai/types';
import api from '../api';
import DreamMarque from './common/DreamMarque';
import { ReactComponent as DreamWordmark } from '../icons/brand/dream-wordmark.svg';
import PublicNoticePage from './common/PublicNoticePage';
import BottomSheet from './common/BottomSheet';
import './circle-invite-page.scss';

/** `shop` is newer than some @dreamai/types releases. */
type CircleInvite = BaseCircleInvite & { shop?: string | null };

/** `noAssistant`: the signed-in account has no assistant (newer @dreamai/types). */
type PreviewResponse = CircleInvitePreviewResponse & { noAssistant?: boolean };

/** A shop-only invite: joins the sender's shop, not each other's Circles. */
function shopOnly(invite: CircleInvite): boolean {
  return !!invite.shop && !invite.allowScheduling && !invite.allowHeadsUps;
}

const SHOP_CAN =
  'add things to it, comment on its meal plan and share your food preferences';

/**
 * The page behind a Circle invite link (`/circle/<code>`). Leads with the
 * group ("Ski trip") or the person ("Ben wants to connect your assistants"),
 * says in one line what the assistants will do, lets the viewer pick how long
 * (the sender's default, editable) and say Yes or No. The longer explanation
 * (both ways, what nobody sees, how to end it) sits behind "How it works".
 * Yes puts both people in each other's Circles and sends anything the sender
 * was holding for them; No changes nothing.
 *
 * A personal link works without signing in (it is for one person, so the
 * link identifies them); a group link asks the visitor to sign in first.
 * Signed in to an account with no assistant, the page warns that nobody's
 * assistant could reach them through it and offers to switch account.
 */

const DAY_MS = 24 * 60 * 60 * 1000;

type Choice = CircleDuration | 'date';

interface DurationOption {
  value: Choice;
  label: string;
  detail: string;
}

function formatDay(date: Date): string {
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

function isoDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The end of `day` (yyyy-mm-dd) in the viewer's own time zone. */
function endOfDay(day: string): Date {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d, 23, 59, 59);
}

/**
 * What the viewer can pick, each with the day it ends. "Until after the
 * meeting" is offered only while the sender is holding one for them.
 */
function durationOptions(invite: CircleInvite, now: Date): DurationOption[] {
  const options: DurationOption[] = [];
  if (invite.heldMeetingEndsAt) {
    const meetingEnds = new Date(invite.heldMeetingEndsAt);
    const what =
      invite.heldMeetingTitles.length === 1
        ? invite.heldMeetingTitles[0]
        : 'the meetings';
    options.push({
      value: 'meeting',
      label: `Until after ${what}`,
      detail: `Ends ${formatDay(new Date(meetingEnds.getTime() + DAY_MS))}`,
    });
  }
  options.push(
    {
      value: 'week',
      label: '1 week',
      detail: `Ends ${formatDay(new Date(now.getTime() + 7 * DAY_MS))}`,
    },
    {
      value: 'month',
      label: '1 month',
      detail: `Ends ${formatDay(new Date(now.getTime() + 30 * DAY_MS))}`,
    },
    {
      value: 'permanent',
      label: 'Permanently',
      detail: 'Until either of you ends it',
    },
    { value: 'date', label: 'Until a date', detail: 'Pick the day it ends' },
  );
  return options;
}

/** The permissions the invite covers, as short labels. */
function covers(invite: CircleInvite): { key: string; label: string }[] {
  const items: { key: string; label: string }[] = [];
  if (invite.allowScheduling) {
    items.push({ key: 'scheduling', label: 'Scheduling' });
  }
  if (invite.allowHeadsUps) items.push({ key: 'messages', label: 'Messages' });
  if (invite.shop) items.push({ key: 'shop', label: invite.shop });
  return items;
}

/** What the assistants will do, as a clause ("find times for you to meet"). */
function purpose(invite: CircleInvite, pair: boolean): string {
  const meet = pair
    ? 'find times for the two of you to meet'
    : 'find times for you to meet';
  if (invite.allowScheduling && invite.allowHeadsUps) {
    return `${meet} and pass messages between you`;
  }
  if (invite.allowHeadsUps) {
    return 'pass a message, a question or a document between you';
  }
  return meet;
}

/** The line under a personal heading: what the two assistants are for. */
function personalLead(invite: CircleInvite): string {
  if (shopOnly(invite)) {
    return `Through your secretary you can ${SHOP_CAN}.`;
  }
  const line = `So they can ${purpose(invite, true)}.`;
  const shop = invite.shop
    ? ` You also join ${invite.fromName}'s ${invite.shop}.`
    : '';
  return invite.allowScheduling
    ? `${line} You still say yes to every meeting.${shop}`
    : `${line}${shop}`;
}

/** The line under the group name: what the sender is setting up. */
function groupLead(invite: CircleInvite): string {
  const { fromName, group, membersConnect } = invite;
  const line = membersConnect
    ? `${fromName} is connecting everyone's secretaries in ${group}, so they can ${purpose(invite, false)}.`
    : `${fromName} is letting people in ${group} connect their secretaries with ${fromName}'s, to ${purpose(invite, false)}.`;
  return invite.allowScheduling
    ? `${line} You still say yes to every meeting.`
    : line;
}

/** The "How it works" sheet: the longer explanation, one point per line. */
function howItWorks(invite: CircleInvite): string[] {
  const { fromName, group, membersConnect } = invite;
  const points: string[] = [];
  const shopPoint = `${invite.shop}: through your secretary you can ${SHOP_CAN}. ${fromName} does the checkout and pays; nothing is bought for you.`;
  if (shopOnly(invite)) {
    return [
      shopPoint,
      `It doesn't let your secretaries schedule with or message each other, and nobody sees the other's calendar, inbox or anything their secretary knows.`,
      'You can leave any time by telling your secretary.',
    ];
  }
  if (group) {
    points.push(
      membersConnect
        ? `Everyone in ${group} who says yes is connected with ${fromName} and with each other.`
        : `This connects you with ${fromName} only, not with others who use the link.`,
    );
  }
  points.push(
    `It works both ways: ${fromName} joins your Circle and you join ${fromName}'s.`,
  );
  if (invite.allowScheduling) {
    points.push(
      `Scheduling: your secretaries can find times for you to meet. You still say yes to every meeting; nothing is booked for you.`,
    );
  }
  if (invite.allowHeadsUps) {
    points.push(
      'Messages: your secretaries can pass each other a message, a question or a document. Nothing is sent without you.',
    );
  }
  if (invite.shop) points.push(shopPoint);
  points.push(
    "Nobody sees the other's calendar, inbox or anything their secretary knows.",
  );
  points.push(
    group
      ? 'You can end it any time by telling your secretary.'
      : 'Either of you can end it any time by telling your secretary.',
  );
  return points;
}

/** One line about what the sender is holding for the viewer, if anything. */
function heldLine(invite: CircleInvite): string | null {
  const held = invite.heldMeetingTitles;
  if (held.length === 0) return null;
  const what =
    held.length === 1
      ? `"${held[0]}" request`
      : `${held.length} meeting requests`;
  return `${invite.fromName}'s ${what} ${held.length === 1 ? 'is' : 'are'} waiting for you and ${held.length === 1 ? 'comes' : 'come'} through when you say yes.`;
}

type Page =
  | { state: 'loading' }
  | { state: 'usable'; invite: CircleInvite; noAssistant: boolean }
  | { state: 'own' }
  | { state: 'invalid' }
  | { state: 'sign_in'; forName?: string }
  | { state: 'accepted'; summary: string; shopOnly: boolean }
  | { state: 'declined' };

const CircleInvitePage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const {
    getAccessTokenSilently,
    isAuthenticated,
    isLoading,
    loginWithRedirect,
  } = useAuth0();
  const [page, setPage] = useState<Page>({ state: 'loading' });
  const [duration, setDuration] = useState<Choice>('permanent');
  const [untilDay, setUntilDay] = useState(() =>
    isoDay(new Date(Date.now() + 14 * DAY_MS)),
  );
  const [accepting, setAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);
  const [showHow, setShowHow] = useState(false);

  // Signed in: act as that account. Otherwise the link itself identifies the
  // person a personal invite is for; a group link asks them to sign in.
  const endpoint = async (path: string) => {
    const base = isAuthenticated ? '/circle-invites' : '/circle-invite-links';
    const headers: Record<string, string> = isAuthenticated
      ? { Authorization: `Bearer ${await getAccessTokenSilently()}` }
      : {};
    return {
      url: `${base}/${encodeURIComponent(code ?? '')}${path}`,
      config: { headers, validateStatus: (status: number) => status < 500 },
    };
  };

  useEffect(() => {
    if (!code || isLoading) return;
    let stale = false;
    const load = async () => {
      try {
        const { url, config } = await endpoint('');
        const { data } = await api.get<PreviewResponse>(url, config);
        if (stale) return;
        if (data.state === 'usable' && data.invite) {
          setDuration(
            data.invite.defaultDuration === 'meeting' &&
              !data.invite.heldMeetingEndsAt
              ? 'permanent'
              : data.invite.defaultDuration,
          );
          setPage({
            state: 'usable',
            invite: data.invite,
            noAssistant: data.noAssistant === true,
          });
        } else if (data.state === 'own') {
          setPage({ state: 'own' });
        } else if (data.state === 'sign_in') {
          const forName = (data as { inviteeName?: string }).inviteeName;
          setPage(
            forName ? { state: 'sign_in', forName } : { state: 'sign_in' },
          );
        } else {
          setPage({ state: 'invalid' });
        }
      } catch (err) {
        console.error('[CircleInvite] Failed to load invite:', err);
        if (!stale) setPage({ state: 'invalid' });
      }
    };
    void load();
    return () => {
      stale = true;
    };
  }, [code, isLoading, isAuthenticated, getAccessTokenSilently]);

  const accept = async () => {
    if (!code || page.state !== 'usable') return;
    setAccepting(true);
    setAcceptError(null);
    try {
      const body: AcceptCircleInviteRequest =
        duration === 'date'
          ? { until: endOfDay(untilDay).toISOString() }
          : { duration };
      const { url, config } = await endpoint('/accept');
      const { data } = await api.post<AcceptCircleInviteResponse>(
        url,
        body,
        config,
      );
      if (data.state === 'linked' && data.link) {
        const link = data.link as typeof data.link & { shop?: string | null };
        setPage({
          state: 'accepted',
          summary: link.summary,
          shopOnly: !!link.shop && !link.allowScheduling && !link.allowHeadsUps,
        });
      } else if (data.state === 'own') {
        setPage({ state: 'own' });
      } else {
        setPage({ state: 'invalid' });
      }
    } catch (err) {
      console.error('[CircleInvite] Failed to accept invite:', err);
      setAcceptError('Something went wrong. Try again in a moment.');
    } finally {
      setAccepting(false);
    }
  };

  if (page.state === 'loading') {
    return <PublicNoticePage loading testId='circle-invite-loading' />;
  }
  if (page.state === 'accepted') {
    return (
      <PublicNoticePage
        tone='success'
        title={page.shopOnly ? "You're in" : "You're connected"}
        body={`${page.summary} You can close this page.`}
        testId='circle-invite-accepted'
      />
    );
  }
  if (page.state === 'declined') {
    return (
      <PublicNoticePage
        title='No problem'
        body='Nothing has changed: your secretaries stay as they are. You can close this page.'
        testId='circle-invite-declined'
      />
    );
  }
  if (page.state === 'sign_in') {
    const firstName = page.forName?.split(/\s+/)[0];
    return (
      <PublicNoticePage
        title={
          page.forName
            ? `This invite is for ${page.forName}`
            : 'Sign in to see this invite'
        }
        body={
          firstName
            ? `If you're ${firstName}, sign in to accept it.`
            : 'Sign in to Dream first so we know who is accepting. It only takes a moment.'
        }
        testId='circle-invite-sign-in'
        actions={
          <button
            type='button'
            className='public-notice__button'
            onClick={() =>
              void loginWithRedirect({
                appState: { path: window.location.pathname },
              })
            }
          >
            Sign in
          </button>
        }
      />
    );
  }
  if (page.state === 'own') {
    return (
      <PublicNoticePage
        title='This is your own invite'
        body="It's for the person you made it for to open. Send them the link and they take it from there. You can close this page."
        testId='circle-invite-own'
      />
    );
  }
  if (page.state === 'invalid') {
    return (
      <PublicNoticePage
        title="This invite isn't available"
        body='It may have been used already, turned off, run out, or be meant for someone else. Ask the person who sent it for a new one. You can close this page.'
        testId='circle-invite-invalid'
      />
    );
  }

  const { invite, noAssistant } = page;
  const switchAccount = () =>
    void loginWithRedirect({
      authorizationParams: { prompt: 'login' },
      appState: { path: window.location.pathname },
    });
  const options = durationOptions(invite, new Date());
  const today = isoDay(new Date());
  const untilValid =
    duration !== 'date' || (untilDay !== '' && untilDay >= today);
  const joining = !!invite.group || shopOnly(invite);
  const yes = joining ? 'Yes, join' : 'Yes, connect';
  const busy = joining ? 'Joining…' : 'Connecting…';

  return (
    <div className='circle-invite' data-testid='circle-invite'>
      <header className='circle-invite__header'>
        <div className='circle-invite__logo' aria-hidden='true'>
          <DreamMarque className='circle-invite__marque' />
          <DreamWordmark className='circle-invite__wordmark' />
        </div>
      </header>
      <main className='circle-invite__main'>
        <section
          className='circle-invite__card'
          aria-labelledby='circle-invite-title'
        >
          <p className='circle-invite__eyebrow'>
            {shopOnly(invite) ? 'Shop invite' : 'Circle invite'}
          </p>
          <h1 id='circle-invite-title' className='circle-invite__title'>
            {invite.group ??
              (shopOnly(invite)
                ? `${invite.fromName} is inviting you to the ${invite.shop}`
                : `${invite.fromName} wants to connect your secretaries`)}
          </h1>
          <p className='circle-invite__lead'>
            {invite.group ? groupLead(invite) : personalLead(invite)}
          </p>
          <ul className='circle-invite__covers' aria-label='Covers'>
            {covers(invite).map((item) => (
              <li
                key={item.key}
                className={`circle-invite__cover circle-invite__cover--${item.key}`}
              >
                {item.label}
              </li>
            ))}
          </ul>
          {heldLine(invite) && (
            <p className='circle-invite__held'>{heldLine(invite)}</p>
          )}
          <button
            type='button'
            className='circle-invite__how'
            onClick={() => setShowHow(true)}
          >
            How it works
          </button>
          <BottomSheet
            open={showHow}
            onClose={() => setShowHow(false)}
            title='How it works'
            className='circle-invite__sheet'
          >
            <ul className='circle-invite__how-list'>
              {howItWorks(invite).map((point) => (
                <li key={point}>{point}</li>
              ))}
            </ul>
          </BottomSheet>

          {!shopOnly(invite) && (
            <fieldset className='circle-invite__duration'>
              <legend className='circle-invite__duration-label'>
                Until when?
              </legend>
              <div
                className='circle-invite__options'
                role='radiogroup'
                aria-label='Until when'
              >
                {options.map((o) => (
                  <button
                    key={o.value}
                    type='button'
                    role='radio'
                    aria-checked={duration === o.value}
                    aria-label={o.label}
                    aria-describedby={`circle-invite-detail-${o.value}`}
                    className={`circle-invite__option${duration === o.value ? ' circle-invite__option--on' : ''}`}
                    onClick={() => setDuration(o.value)}
                    disabled={accepting}
                  >
                    <span
                      className='circle-invite__option-dot'
                      aria-hidden='true'
                    />
                    <span className='circle-invite__option-text'>
                      <span className='circle-invite__option-label'>
                        {o.label}
                      </span>
                      <span
                        id={`circle-invite-detail-${o.value}`}
                        className='circle-invite__option-detail'
                      >
                        {o.detail}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
              {duration === 'date' && (
                <label className='circle-invite__date'>
                  <span>Ends on</span>
                  <input
                    type='date'
                    value={untilDay}
                    min={today}
                    onChange={(e) => setUntilDay(e.target.value)}
                    disabled={accepting}
                  />
                </label>
              )}
            </fieldset>
          )}

          {acceptError && (
            <p className='circle-invite__error' role='alert'>
              {acceptError}
            </p>
          )}

          {noAssistant && (
            <p className='circle-invite__warning' role='note'>
              This account doesn&apos;t have an assistant, so {invite.fromName}
              &apos;s assistant can&apos;t reach you through it. Sign in with
              the account you use with your assistant.
            </p>
          )}

          <div className='circle-invite__actions'>
            {noAssistant && (
              <button
                type='button'
                className='circle-invite__button circle-invite__button--primary'
                onClick={switchAccount}
                disabled={accepting}
              >
                Switch account
              </button>
            )}
            <button
              type='button'
              className={`circle-invite__button circle-invite__button--${noAssistant ? 'secondary' : 'primary'}`}
              onClick={() => void accept()}
              disabled={accepting || !untilValid}
            >
              {accepting ? busy : noAssistant ? 'Accept anyway' : yes}
            </button>
            <button
              type='button'
              className='circle-invite__button circle-invite__button--secondary'
              onClick={() => setPage({ state: 'declined' })}
              disabled={accepting}
            >
              No thanks
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default CircleInvitePage;
